import {Poi, Trip} from '../../../react-app-env';
import React, {ReactNode} from 'react';
import SidebarHeader, {buildPopStackButton} from '../../sidebar/SidebarHeader';
import {SidebarPage} from '../../sidebar/SidebarPage';
import {StackApi} from '../../stack/Stack.types';
import {PoiCategory, PoiCategoryGroup} from '../../../types/poi.types';
import ListViewGroup from '../../listview/listviewgroup/ListViewGroup';
import ListView from '../../listview/ListView';
import {i18n} from '../../../i18n/i18n';
import ListViewItem from '../../listview/listviewitem/ListViewItem';
import {noop} from '../../../utils/Utils';
import {getPoiCategoryIconCategory} from '../../../utils/PoiCategoryUtils';
import {IconSizes} from '../../icon/Icon';
import SidebarScrollableContent from '../../sidebar/SidebarScrollableContent';
import {ListViewItemDecoration, ListViewItemDecorationIcon} from '../../listview/listviewitem/ListViewItem.types';
import {Api} from '../../../api/Api';

export interface PoiCategorySidebarPageProps {
  trip: Trip;
  poi: Poi;
  stackApi: StackApi;
}

const PoiCategorySidebarPage: React.FunctionComponent<PoiCategorySidebarPageProps> = ({ trip, poi, stackApi }) => {

  const generalPoiCategoryMatcher = (poiCategory: PoiCategory) => {
    return poiCategory.indexOf('_') === -1;
  };
  const prefixPoiCategoryMatcher = (poiCategory: PoiCategory, prefix: string) => {
    return poiCategory.startsWith(prefix);
  };

  const categoryReferences = trip.pois.map(poi => poi.category);
  const categoryReferenceCountMap = categoryReferences.reduce((arr, each) => {
    arr[each] = (arr[each] ?? 0) + 1;
    return arr;
  }, {} as { [key: string]: number });

  const popularCategories = Object
      .entries(categoryReferenceCountMap)
      .map(entry => {
        return { category: entry[0] as PoiCategory, count: entry[1] };
      })
      .filter(each => each.category !== PoiCategory.GENERIC)
      .filter((each) => each.count >= 5)
      .sort((a, b) => b.count - a.count)
      .map(each => each.category)
      .slice(0, 5);

  function renderPoiCategory(category: PoiCategory, bottomAffix?: string): ReactNode {
    const icon = getPoiCategoryIconCategory(category);
    const decoration: ListViewItemDecoration = {
      icon: poi.category === category ? ListViewItemDecorationIcon.OPTION_SELECTED : ListViewItemDecorationIcon.OPTION_EMPTY,
      size: IconSizes.MEDIUM
    };

    return <ListViewItem
        key={category}
        title={i18n().poiCategories[category]}
        bottomAffix={bottomAffix ? { label: bottomAffix } : undefined}
        iconCategory={icon}
        decoration={decoration}
        onClick={() => {
          Api.updatePoiCategory(poi, category).then(() => {
            stackApi.pop();
          });
        }}/>;
  }

  function renderPoiCategoryGroup(categoryGroup: PoiCategoryGroup): ReactNode {
    const matcher = categoryGroup === PoiCategoryGroup.GENERAL
        ? (each: PoiCategory) => generalPoiCategoryMatcher(each)
        : (each: PoiCategory) => prefixPoiCategoryMatcher(each, categoryGroup + '_');
    const categories = Object.values(PoiCategory).filter(matcher);

    return <ListViewGroup key={categoryGroup}
                          title={i18n().poiCategoryGroups[categoryGroup]}>
      {categories.map((category) => renderPoiCategory(category))}
    </ListViewGroup>;
  }

  function renderPopularCategories(): ReactNode {
    if (trip.pois.length < 5) {
      return null;
    }

    return <ListViewGroup key={'popular'}
                          title={i18n().poiDetails.poiCategory.popularCategories}>
      {popularCategories.map((category) => {
        const usageCount = categoryReferenceCountMap[category] || 0;
        const usageLabel = i18n().poiDetails.poiCategory.usageCount.replace('%count%', usageCount.toString());
        return renderPoiCategory(category, usageLabel);
      })}
    </ListViewGroup>;
  }

  return <SidebarPage className="poi-category">
    <SidebarHeader
        title={i18n().poiDetails.poiCategory.title}
        subtitle={poi.title}
        buttons={[
          buildPopStackButton(() => stackApi.pop())
        ]}/>
    <SidebarScrollableContent>
      <ListView>
        {renderPopularCategories()}
        {renderPoiCategoryGroup(PoiCategoryGroup.GENERAL)}
        {renderPoiCategoryGroup(PoiCategoryGroup.GEOGRAPHY)}
        {renderPoiCategoryGroup(PoiCategoryGroup.ACTIVITY)}
        {renderPoiCategoryGroup(PoiCategoryGroup.CULINARY)}
        {renderPoiCategoryGroup(PoiCategoryGroup.TRANSPORTATION)}
      </ListView>
    </SidebarScrollableContent>
  </SidebarPage>;
};

export default PoiCategorySidebarPage;
